import { Form, Input, Modal } from 'antd';
import { LockOutlined } from '@ant-design/icons';

import styles from './PasswordProtection.module.css';
import { useMemo, useState } from 'react';
import useGiidePasswordOperation from './useGiidePasswordOperation';
import { useDispatch, useSelector } from 'react-redux';
import { IState, PasswordModalVariants } from '../../graphql/types';
import { lockGiide, setGiidePasswordModal, unlockGiide } from '../../actions';
import useGiideList from '../../hooks/useQuery/useGiideList';
import { GiidePasswordAction } from '../../graphql/api';

const GiidePasswordModal = () => {
  const [form] = Form.useForm();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionSuccess, setSubmissionSuccess] = useState<string | null>(
    null
  );
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();

  const { passwordOperation } = useGiidePasswordOperation();

  const { giideId, modalType } = useSelector<
    IState,
    { giideId: string | null; modalType?: PasswordModalVariants }
  >(state => state.giidePasswordModal);

  const { refetch } = useGiideList(true);

  const onFormSubmit = async (action: GiidePasswordAction) => {
    try {
      setSubmissionError(null);
      setSubmitting(true);

      await form.validateFields();

      const values = form.getFieldsValue();

      const result = await passwordOperation(giideId, action, values.password);

      if (result.error) {
        throw new Error(result.error);
      }

      let successMessage: string | null = null;
      switch (modalType) {
        case PasswordModalVariants.Create:
          successMessage = 'Password creation successful.';
          if (giideId) {
            dispatch(lockGiide(giideId));
          }
          break;
        case PasswordModalVariants.Delete:
          successMessage = 'Password removal successful.';
          break;
        case PasswordModalVariants.Check:
          if (giideId) {
            dispatch(unlockGiide(giideId));
          }
          successMessage =
            'Password verified. Click the giide to view or edit.';
          break;
      }

      setSubmissionSuccess(successMessage);

      // Refetch the giide list to reflect the changes
      refetch();
    } catch (error) {
      console.log(error);

      if (error instanceof Error) {
        setSubmissionError(error.message);
      } else {
        setSubmissionError('Something went wrong completing the request.');
      }
    } finally {
      setSubmitting(false);
      form.resetFields();
    }
  };

  const onClose = () => {
    form.resetFields();
    setSubmissionError(null);
    setSubmissionSuccess(null);
    dispatch(setGiidePasswordModal(null, undefined));
  };

  const onOk = async () => {
    if (submissionSuccess) {
      onClose();
      return;
    }

    switch (modalType) {
      case PasswordModalVariants.Create:
        await onFormSubmit(GiidePasswordAction.create);
        break;
      case PasswordModalVariants.Delete:
        await onFormSubmit(GiidePasswordAction.remove);
        break;
      case PasswordModalVariants.Check:
        await onFormSubmit(GiidePasswordAction.check);
        break;
    }
  };

  const okText = useMemo(() => {
    if (submissionSuccess) {
      return 'Ok';
    }

    switch (modalType) {
      case PasswordModalVariants.Create:
        return 'Lock giide';
      case PasswordModalVariants.Check:
        return 'Unlock giide';
      case PasswordModalVariants.Delete:
        return 'Remove Password Protection';
      default:
        return null;
    }
  }, [modalType, submissionSuccess]);

  const titleText = useMemo(() => {
    switch (modalType) {
      case PasswordModalVariants.Create:
        return 'Lock giide';
      case PasswordModalVariants.Check:
        return 'Unlock giide';
      case PasswordModalVariants.Delete:
        return 'Remove Password Protection';
      default:
        return null;
    }
  }, [modalType]);

  const explanationText = useMemo(() => {
    switch (modalType) {
      case PasswordModalVariants.Create:
        return 'Secure your giide with a password. Enter it for future access or modifications.';
      case PasswordModalVariants.Check:
        return 'Access this giide by providing the correct password.';
      case PasswordModalVariants.Delete:
        return 'Remove password protection from this giide, allowing anyone with account access to view and edit it.';
      default:
        return null;
    }
  }, [modalType]);

  return (
    <Modal
      title={titleText}
      cancelText={'Cancel'}
      okText={okText}
      open={modalType !== undefined}
      onOk={onOk}
      okType={
        modalType === PasswordModalVariants.Delete && !submissionSuccess
          ? 'danger'
          : undefined
      }
      onCancel={onClose}
      confirmLoading={submitting}
    >
      {!submissionSuccess && (
        <Form form={form} layout={'vertical'} style={{ marginTop: '24px' }}>
          <div className={styles.formDescription}>{explanationText}</div>
          <Form.Item
            name={'password'}
            rules={[
              { required: true, message: 'Please input a password' },
              { min: 8, message: 'Password must be at least 8 characters' },
              {
                max: 32,
                message: 'Password must be no longer than 32 characters',
              },
            ]}
          >
            <Input
              prefix={<LockOutlined className={styles.lockIcon} />}
              type="password"
              placeholder="Password"
            />
          </Form.Item>

          {modalType === PasswordModalVariants.Create && (
            <Form.Item
              name={'confirmPassword'}
              dependencies={['password']}
              rules={[
                {
                  required: true,
                  message: 'Please confirm your password',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Password do not match'));
                  },
                }),
              ]}
            >
              <Input
                prefix={<LockOutlined className={styles.lockIcon} />}
                type="password"
                placeholder="Confirm Password"
              />
            </Form.Item>
          )}
        </Form>
      )}

      <div className={styles.submissionFeedback}>
        {submissionError && (
          <div className={styles.submissionError}>{submissionError}</div>
        )}
        {submissionSuccess && (
          <div className={styles.submissionSuccess}>{submissionSuccess}</div>
        )}
      </div>
    </Modal>
  );
};

export default GiidePasswordModal;
